import { OrderType } from '../../types/route.types';
import { Coordinates, Currency } from '../general/general.types';
import { PaymentAccountType } from '../payment/payment.types';
import {
  ConcessionStore,
  DeliveryType,
  PickupLocationType,
  ServiceChargeConfig,
  TableServiceCategory,
} from '../restaurant/restaurant.types';

// #region order enum
export enum OrderState {
  Created = 0,
  ReadyToProcess = 1,
  AcceptedByRestaurant = 2,
  Dispatched = 3,
  Delivered = 4,
  Cancelled = 5,
  ManualReview = 6,
  PlacedCanBeCancelled = 7,
  RejectedByRestaurant = 8,
  RejectedByFlipdish = 9,
  RejectedAutomatically = 10,
  RejectedAfterBeingAccepted = 11,
  AcceptedAndRefunded = 12,
  PendingPayment = 20,
}
// #endregion

// #region order types
export interface OrderCommon {
  DeliveryType: DeliveryType;
  deviceId: string; // UUID
  MenuId?: number;
  OrderItemVms: OrderItem[];
  PaymentAccountId?: number;
  RequestedDeliveryOrPickupTimeUtc?: string | null;
  TipAmount: number;
  VersionGuid?: string;
  VirtualRestaurantId: number;
}

export interface DeliveryOrder extends OrderCommon {
  DeliveryLocationId: number;
  UserLocation?: Coordinates;
}

export interface CollectionOrder extends OrderCommon {
  PhysicalRestaurantId: number;
  PickupLocationId?: number;
  PickupLocationType: PickupLocationType;
  TableServiceCatagory?: TableServiceCategory;
}

export interface OrderItem {
  MenuItemId: number;
  MenuItemOptionSetItemIds: number[];
}
// #endregion

// #region created order types
export interface CreatedOrder {
  OrderId: number;
  SaveOrderResponseStatus: number;
  ServiceCharge: ServiceChargeConfig;
  VoucherBasketItems: VoucherBasketItem[];
}
// #endregion

// #region submitted order types
export interface SubmittedOrder {
  CancelOrderPeriodSeconds?: number | null; // If null then don't display cancel control to user.
  DeliveryTimeEstimateMinutes?: number | null;
  DeliveryType: DeliveryType;
  DisplayRateAppControl: boolean;
  DisplayTipControl: boolean;
  LinkText: string;
  LinkUrl: string;
  LocalOrderId: string;
  OrderId: number;
  OrderPlacedMessage: string;
  PickupCode: string; // Pickup Only - the code to display when picking up order
  RedirectUri: string; // Redirect payment methods only
  RestaurantAddress: string; // Pickup Only
  RestaurantName: string;
}
// #endregion

// #region voucher types
export interface VoucherBasketItem {
  Amount: number;
  Description: string;
  Name: string;
  TaxAmount: number;
}
// #endregion

// #region order history types
export interface OrderHistory {
  Coordinates: Coordinates;
  Currency: Currency;
  DeliveryAmount: number;
  DeliveryLocationAddressString: string | null;
  DeliveryLocationId: number;
  DeliveryTaxAmount: number;
  DeliveryType: DeliveryType;
  FoodAmount: number;
  FoodTaxAmount: number;
  FulfillmentStatus?: FulfilmentStatus;
  IsHiddenFromRecentOrdersList: boolean;
  IsoCurrency: string;
  OnlineOrderingFee: number;
  OrderHistoryItems: OrderHistoryItems[];
  OrderId: number;
  OrderState: OrderState;
  PaymentAccountDescription: string;
  PaymentAccountType: PaymentAccountType;
  PaymentProcessingResponse: string | null;
  PhysicalRestaurantId: number;
  PickupLocationAddressString: string | null;
  PickupLocationId: number;
  PickupLocationType: PickupLocationType;
  ReceiptCode: string | null;
  RefundedAmount: number | null;
  RestaurantName: string;
  TableServiceCatagory: TableServiceCategory;
  TipAmount: number;
  TipTaxAmount: number;
  TotalAmount: number;
  TotalTax: number | null;
  TsOrderPlaced: string;
  UserLocation: Coordinates;
  VirtualRestaurantId: number;
  VoucherAmount: number | null;
  VoucherCode: string | null;
}

export interface OrderHistoryItems {
  ConcessionStore: ConcessionStore;
  Description: string;
  IsAvailable: boolean;
  MenuId: number;
  MenuItemId: number;
  MenuSectionId: number;
  MenuSectionName: string;
  Metadata: { [key: string]: string };
  Name: string;
  OrderHistoryItemOptions: OrderHistoryItemOptions[];
  OrderItemId: number;
  Price: number;
  PriceIncludingOptionSetItems: number;
  Quantity?: number;
  VirtualRestaurantId: number;
}

export interface OrderHistoryItemOptions {
  IsMasterOptionSetItem: boolean;
  MenuItemOptionDisplayOrder: number;
  MenuItemOptionId: number;
  MenuItemOptionPublicId: string;
  MenuItemOptionSetDisplayOrder: number;
  Metadata: { [key: string]: string };
  Name: string;
  Price: number;
}

export interface GetOrderHistoryQueryParams {
  count?: number;
  deliveryType?: OrderType;
  skip: number;
  wlid: number;
}
// #endregion

// #region order status types
export interface FulfilmentStatus {
  DeliveryTrackingUrl: string;
  Description: string;
  EstimatedCompletionDescription: string;
  EstimatedCompletionTime: string;
  EstimatedRemainingCompletionMinutes: number;
  Icon: string;
  ImageUrl: string;
  IsError: boolean;
  StatusId: string;
  StatusName: string;
  StatusPosition: number;
  TotalStatuses: number;
}

export interface GetFulfilmentStatusQueryParams {
  orderId: number;
}
// #endregion

// #region reorder types
export interface Reorder {
  OrderId: number;
  Currency: {
    Id: number;
    IsoCode: string;
  };
  TotalAmount: number;
  MenuItems: ReorderMenuItem[];
  Store: {
    Id: number;
    Name: string;
    StoreGroupId: number;
  };
  Fulfilment: {
    Type: number;
    DeliveryLocationId: number;
  };
}

export interface ReorderMenuItem {
  Available: boolean;
  ConcessionStoreId: number;
  Id: number;
  MenuId: number;
  MenuItemPersistentId: string;
  Name: string;
  OptionSets: ReorderOptionSet[];
}

interface ReorderOptionSet {
  Id: number;
  MenuItemOptionSetItemPersistentId: string;
}

export interface GetReorderQueryParams {
  orderId: number;
}
export interface GetReordersQueryParams {
  count?: number;
  cutDate?: Date;
  fulfilmentType?: DeliveryType;
  storeIds?: number[];
}
// #endregion
